import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import logoDark  from '../assets/logo-dark.png';
import logoWhite from '../assets/logo-white.png';
import iconTank    from '../assets/loading/oil-tank.png';
import iconDrone   from '../assets/loading/drone.png';
import iconMonitor from '../assets/loading/monitor.png';
import './LoadingScreen.css';

const CYCLE_MS = 1500; // total animation cycle duration in ms

/**
 * LoadingScreen
 *
 * Shows a full-screen branded animation while `isLoading` is true.
 * When `isLoading` becomes false the animation finishes its current
 * cycle (so it never cuts off mid-transition) then calls `onDone`.
 *
 * Props:
 *   isLoading  {boolean}   — keep showing while true
 *   onDone     {function}  — called after the cycle completes
 */
export default function LoadingScreen({ isLoading, onDone }) {
  const { theme } = useTheme();
  const logo = theme === 'dark' ? logoWhite : logoDark;

  // Capture the moment this component mounted — used to calculate
  // where we are in the animation cycle when loading finishes.
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    if (!isLoading) {
      const elapsed = Date.now() - mountTimeRef.current;

      // If loading resolved almost instantly (< 200 ms) skip the
      // animation entirely so the user doesn't wait needlessly.
      if (elapsed < 200) {
        onDone?.();
        return;
      }

      // Otherwise wait until the current cycle boundary.
      const timeInCycle = elapsed % CYCLE_MS;
      const remaining   = CYCLE_MS - timeInCycle;
      // Guard: if we're within 100 ms of the boundary, roll over to
      // the next cycle so we don't clip the very last transition.
      const waitMs = remaining < 100 ? remaining + CYCLE_MS : remaining;

      const timer = setTimeout(() => onDone?.(), waitMs);
      return () => clearTimeout(timer);
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ls-overlay">
      <div className="ls-stage">
        {/* Image 4 — Terra logo (theme-aware) */}
        <img
          src={logo}
          alt="Terra Inspectioneering"
          className="ls-icon ls-logo"
        />

        {/* Image 1 — Oil / storage tank */}
        <img
          src={iconTank}
          alt=""
          className="ls-icon ls-tank ls-themed"
        />

        {/* Image 2 — Drone */}
        <img
          src={iconDrone}
          alt=""
          className="ls-icon ls-drone ls-themed"
        />

        {/* Image 3 — Report / monitor */}
        <img
          src={iconMonitor}
          alt=""
          className="ls-icon ls-monitor ls-themed"
        />
      </div>
    </div>
  );
}
